import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// reactstrap components
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Alert,
} from 'reactstrap';

// core components
import { useAuth } from 'contexts/AuthContext';
import ConfirmationModal from '../ConfirmationModal';
import SuccessModal from '../SuccessModal';

// APIs
import notesApi from '../../service/notesApi';
// COMENTADO - Solo usar token de Keycloak via useAuth, no getAuthToken
// import { getAuthToken } from 'config/apiConfig';
// COMENTADO - Solo usar token de Keycloak via useAuth, no getAuthToken
// import { getAuthToken } from 'config/apiConfig';

// Components
import NotesSearchFilters from './NotesSearchFilters';

/**
 * NotesPanel - Simple and stable component for managing notes
 */
const NotesPanel = ({
  tag,
  referenceId,
  originType,
  originId,
  title = 'Notes',
  onNoteAdded,
  onNoteUpdated,
  onNoteDeleted,
  showAdvancedSearch: initialShowAdvancedSearch = false,
  systemTags = null, // Nuevo prop para systemTags específicos
}) => {
  // Authentication context
  const { user, getToken } = useAuth();

  // Simple state management
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newUserTags, setNewUserTags] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNote, setEditingNote] = useState('');
  const [editingUserTags, setEditingUserTags] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);

  // Advanced search state
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);
  const [searchFilters, setSearchFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  const handleTagInputChange = (value, setFunc) => {
    // limitar total del input
    if (value.length > 255) value = value.slice(0, 255);
  
    // dividir en tags
    let tags = value.split(',').map(tag => tag.trim());
  
    // aplicar límite de 25 caracteres por tag
    tags = tags.map(tag => tag.slice(0, 25));
  
    // reconstruir string, pero solo permitir nuevo tag si termina en coma o si no pasa de 25 chars
    const lastTag = tags[tags.length - 1];
    if (lastTag && lastTag.length >= 25 && !value.endsWith(',')) {
      tags[tags.length - 1] = lastTag.slice(0, 25);
    }
  
    setFunc(tags.join(', '));
  };
  

  /**
   * Get system tag based on origin type and context
   */
  const getSystemTagFromOriginType = (originType) => {
    // Si se proporcionan systemTags específicos, usarlos
    if (systemTags && Array.isArray(systemTags)) {
      return systemTags;
    }
    
    // Lógica por defecto basada en originType
    switch (originType) {
      case 'MERCHANT':
        return ['MERCHANT'];
      case 'APPLICATION':
        return ['APPLICATION'];
      case 'OWNER':
        // Para owners, necesitamos determinar el contexto
        // Si estamos en un merchant, usar MERCHANT/OWNER
        // Si estamos en una application, usar APPLICATION/OWNER
        // Por defecto, no filtrar por systemTag para owners (mostrar todas)
        return null;
      default:
        return null;
    }
  };

  /**
   * Fetch notes from API using search endpoint with proper filters
   */
  const fetchNotes = async () => {
    if (!tag) return;
    
    // CRITICAL: Do not fetch notes if originId is missing or invalid
    // This prevents showing notes from other entities
    if (!originId || originId === '' || originId === null || originId === undefined) {
      console.warn('⚠️ Cannot fetch notes: originId is required but missing');
      setNotes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // MODIFICADO - Usar token de Keycloak via useAuth
      const token = getToken();

      
      if (!token) {
        setError('No authentication token available');
        return;
      }

      // Usar searchNotes con filtros específicos según el formato de Postman
      const systemTagArray = getSystemTagFromOriginType(originType);
      const searchCriteria = {
        page: 0,
        size: 100, // Obtener más notas para el contexto específico
        sort: 'createdDate,desc',
        // Añadir filtros de contexto si están disponibles
        ...(originType && { originType }),
        // ALWAYS include originId - it's mandatory for proper filtering
        originId: originId,
        // Incluir systemTags correctos según el flujo
        // Solo filtrar por systemTags si se proporcionan específicamente
        ...(systemTagArray && { systemTags: systemTagArray }),
        // Asegurar que solo se muestren notas activas
        active: true
      };

      console.log('🔍 Fetching notes with search criteria:', searchCriteria);
      console.log('🏷️ SystemTags being used for filtering:', systemTagArray);
      const response = await notesApi.searchNotes(searchCriteria);
      
      let notesData = [];
      if (Array.isArray(response)) {
        notesData = response;
      } else if (response && typeof response === 'object') {
        notesData = response?.content || response?.data || [];
      } else {
        notesData = [];
      }
      
      // Asegurar que notesData sea siempre un array
      notesData = Array.isArray(notesData) ? notesData : [];
      
      // Filtrar notas inactivas (active: false)
      const activeNotes = notesData.filter(note => note.active !== false);
      
      // Filtrar por systemTags si se proporcionan específicamente
      let filteredNotes = activeNotes;
      if (systemTagArray && systemTagArray.length > 0) {
        console.log('🔍 Client-side filtering with systemTags:', systemTagArray);
        console.log('📝 Notes before filtering:', activeNotes.length);
        filteredNotes = activeNotes.filter(note => {
          // Si la nota no tiene systemTags, mostrarla (comportamiento para notas existentes)
          if (!note.systemTags || !Array.isArray(note.systemTags) || note.systemTags.length === 0) {
            console.log('✅ Note without systemTags included:', note.id);
            return true;
          }
          // Si la nota tiene systemTags, verificar que coincidan exactamente con los requeridos
          // Para systemTags específicos como ["APPLICATION", "OWNER"], solo mostrar notas que tengan exactamente esos tags
          const matches = systemTagArray.every(requiredTag => note.systemTags.includes(requiredTag)) &&
                         note.systemTags.every(noteTag => systemTagArray.includes(noteTag));
          console.log(`🔍 Note ${note.id} systemTags: [${note.systemTags.join(', ')}] - Required: [${systemTagArray.join(', ')}] - Matches: ${matches}`);
          return matches;
        });
        console.log('📝 Notes after filtering:', filteredNotes.length);
      }
      
      // Preservar información de edición local si existe
      const notesWithLocalEdits = filteredNotes.map(serverNote => {
        // Buscar si existe una versión local de esta nota con información de edición
        const localNote = notes.find(localNote => localNote.id === serverNote.id);
        
        if (localNote && localNote.updatedAt && localNote.updatedBy) {
          // Si la nota local tiene información de edición más reciente, usarla
          const localUpdateTime = new Date(localNote.updatedAt).getTime();
          const serverUpdateTime = new Date(serverNote.lastModifiedDate || serverNote.updatedAt || 0).getTime();
          
          if (localUpdateTime > serverUpdateTime) {
            return {
              ...serverNote,
              lastModifiedDate: localNote.updatedAt,
              lastModifiedBy: localNote.updatedBy,
              updatedAt: localNote.updatedAt,
              updatedBy: localNote.updatedBy
            };
          }
        }
        
        return serverNote;
      });
      
      setNotes(notesWithLocalEdits);
            
            
      // Marcar que ya se cargó la primera vez
      setHasLoaded(true);
      
    } catch (err) {
      console.error('❌ Error fetching notes:', err);
      setError(err.message || 'Failed to fetch notes');
      
      // En caso de error, establecer notas vacías en lugar de mock data
      setNotes([]);
      setHasLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Advanced search with multiple filters
   */
  const handleAdvancedSearch = async (filters, page = 0) => {
    setIsSearching(true);
    setError(null);

    try {
      // MODIFICADO - Usar token de Keycloak via useAuth
      const token = getToken();
      
      if (!token) {
        setError('No authentication token available');
        return;
      }

      // Construir payload de b fasqueda asegurando campos base y condicionales
      const systemTagArray = getSystemTagFromOriginType(originType);
      const searchCriteria = {
        page: page,
        size: pageSize,
        sort: 'createdDate,desc',
        // Campos base siempre enviados
        ...(originType && { originType }),
        ...(originId && { originId }),
        // Solo filtrar por systemTags si se proporcionan específicamente
        ...(systemTagArray && { systemTags: systemTagArray }),
        active: true,
      };

      // Agregar condicionalmente los filtros seleccionados por el usuario
      const effectiveFilters = { ...(searchFilters || {}), ...(filters || {}) };
      if (effectiveFilters) {
        // message
        if (effectiveFilters.message) {
          searchCriteria.message = effectiveFilters.message;
        }
        // createdBy
        if (effectiveFilters.createdBy) {
          searchCriteria.createdBy = effectiveFilters.createdBy;
        }
        // tag -> userTags
        if (Array.isArray(effectiveFilters.userTags) && effectiveFilters.userTags.length > 0) {
          searchCriteria.userTags = effectiveFilters.userTags;
        }
        // createdAt range -> createdDateFrom / createdDateTo con formato exacto
        if (effectiveFilters.createdAt && (effectiveFilters.createdAt.value || effectiveFilters.createdAt.value2)) {
          const normalizeDate = (d) => {
            if (!d) return undefined;
            // Aceptar formatos YYYY-MM-DD o MM/DD/YYYY
            if (d.includes('/')) {
              const [mm, dd, yyyy] = d.split('/');
              if (yyyy && mm && dd) return `${yyyy}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`;
            }
            return d;
          };
          const fromDate = normalizeDate(effectiveFilters.createdAt.value);
          const toDate = normalizeDate(effectiveFilters.createdAt.value2);
          const from = fromDate ? `${fromDate}T00:00:00Z` : undefined;
          const to = toDate ? `${toDate}T23:59:59Z` : undefined;
          if (from) searchCriteria.createdDateFrom = from;
          if (to) searchCriteria.createdDateTo = to;
        }
      }

      const response = await notesApi.searchNotes(searchCriteria);
      
      let searchData = [];
      if (Array.isArray(response)) {
        searchData = response;
      } else if (response && typeof response === 'object') {
        searchData = response?.content || response?.data || [];
        setTotalElements(response?.totalElements || 0);
        setCurrentPage(response?.page || page);
      } else {
        searchData = [];
      }
      
      // Filtrar notas inactivas
      const activeSearchResults = searchData.filter(note => note.active !== false);
      
      // Filtrar por systemTags si se proporcionan específicamente
      let filteredSearchResults = activeSearchResults;
      if (systemTagArray && systemTagArray.length > 0) {
        filteredSearchResults = activeSearchResults.filter(note => {
          // Si la nota no tiene systemTags, mostrarla (comportamiento para notas existentes)
          if (!note.systemTags || !Array.isArray(note.systemTags) || note.systemTags.length === 0) {
            return true;
          }
          // Si la nota tiene systemTags, verificar que coincidan exactamente con los requeridos
          // Para systemTags específicos como ["APPLICATION", "OWNER"], solo mostrar notas que tengan exactamente esos tags
          return systemTagArray.every(requiredTag => note.systemTags.includes(requiredTag)) &&
                 note.systemTags.every(noteTag => systemTagArray.includes(noteTag));
        });
      }
      
      // Preservar información de edición local si existe
      const searchResultsWithLocalEdits = filteredSearchResults.map(serverNote => {
        // Buscar si existe una versión local de esta nota con información de edición
        const localNote = notes.find(localNote => localNote.id === serverNote.id);
        
        if (localNote && localNote.updatedAt && localNote.updatedBy) {
          // Si la nota local tiene información de edición más reciente, usarla
          const localUpdateTime = new Date(localNote.updatedAt).getTime();
          const serverUpdateTime = new Date(serverNote.lastModifiedDate || serverNote.updatedAt || 0).getTime();
          
          if (localUpdateTime > serverUpdateTime) {
            return {
              ...serverNote,
              lastModifiedDate: localNote.updatedAt,
              lastModifiedBy: localNote.updatedBy,
              updatedAt: localNote.updatedAt,
              updatedBy: localNote.updatedBy
            };
          }
        }
        
        return serverNote;
      });
      
      setSearchResults(searchResultsWithLocalEdits);
      
      console.log('✅ Search completed successfully:', activeSearchResults);
      
    } catch (err) {
      console.error('❌ Error searching notes:', err);
      setError(err.message || 'Failed to search notes');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Handle filter changes from search component
   */
  const handleFilterChange = (newFilters) => {
    setSearchFilters(newFilters);
  };

  /**
   * Clear search and show all notes
   */
  const handleClearSearch = () => {
    setSearchFilters({});
    setSearchResults([]);
    setCurrentPage(0);
    setTotalElements(0);
    // Volver a cargar notas normales
    fetchNotes();
  };

  /**
   * Toggle advanced search visibility
   */
  const toggleAdvancedSearch = () => {
    setShowAdvancedSearch(!showAdvancedSearch);
    if (showAdvancedSearch) {
      // Si se está ocultando, limpiar búsqueda
      handleClearSearch();
    }
  };

  /**
   * Toggle add note form visibility
   */
  const toggleAddNoteForm = () => {
    setShowAddNoteForm(prev => !prev);
    if (!showAddNoteForm) {
      // Si se está mostrando, limpiar el formulario
      setNewNote('');
      setNewUserTags('');
    }
  };

  /**
   * Add new note
   */
  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setIsSubmitting(true);

    try {
      const token = getToken();
      
      if (!token) {
        setError('No authentication token available');
        return;
      }

      // Procesar userTags: convertir string separado por comas en array
      const userTagsArray = newUserTags.trim() 
        ? newUserTags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];

             const systemTagArray = getSystemTagFromOriginType(originType);
             const noteData = {
         message: newNote.trim(),
         tag: tag || null,
         originType: originType || null,
         originId: originId || referenceId || null,
         systemTags: systemTagArray || [], // Usar systemTags específicos según el contexto
         userTags: userTagsArray,
         user: user?.preferred_username || user?.name || 'current-user'
       };

      console.log('📝 Creating note with data:', noteData);
      console.log('🏷️ SystemTags calculated:', systemTagArray, 'from originType:', originType);
      const response = await notesApi.createNote(noteData);

      const createdNote = {
        id: response.id || Date.now().toString(),
        message: response.message || newNote.trim(),
        tag: response.tag || tag,
        originType: response.originType || originType,
        originId: response.originId || originId || referenceId,
        userTags: response.userTags || userTagsArray,
        systemTags: response.systemTags || systemTagArray || [],
        active: response.active !== undefined ? response.active : true,
        createdAt: response.createdDate || response.createdAt || new Date().toISOString(),
        createdBy: response.createdBy || user?.preferred_username || 'current-user',
        updatedAt: response.lastModifiedDate || response.updatedAt || new Date().toISOString(),
        updatedBy: response.lastModifiedBy || response.updatedBy || user?.preferred_username || 'current-user'
      };

      setNotes(prevNotes => [createdNote, ...prevNotes]);
      setNewNote('');
      setNewUserTags('');
      
      // Mostrar modal de éxito
      setShowSuccessModal(true);
      
      // Cerrar automáticamente después de 3 segundos
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
      
      if (onNoteAdded) {
        onNoteAdded(createdNote);
      }
    } catch (err) {
      console.error('❌ Error creating note:', err);
      setError('Failed to add note. Please try again.');
      setError('Failed to add note. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Update note
   */
  const handleUpdateNote = async () => {
    if (!editingNote.trim() || !editingNoteId) return;

    try {
      const token = getToken();
      
      if (!token) {
        setError('No authentication token available');
        return;
      }

      // Procesar userTags: convertir string separado por comas en array
      const userTagsArray = editingUserTags.trim() 
        ? editingUserTags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];

      // Obtener la nota original para preservar los campos de filtrado
      const originalNote = notes.find(note => note.id === editingNoteId);
      if (!originalNote) {
        setError('Note not found');
        return;
      }

      // Solo actualizar message, userTags y el usuario que editó
      // Preservar todos los campos de filtrado (originType, originId, systemTags, tag)
      const noteData = {
        message: editingNote.trim(),
        userTags: userTagsArray,
        // Preservar campos de filtrado del original
        originType: originalNote.originType,
        originId: originalNote.originId,
        systemTags: originalNote.systemTags || [],
        tag: originalNote.tag,
        // Usuario que está editando
        user: user?.preferred_username || user?.name || 'current-user'
      };


      await notesApi.updateNote(editingNoteId, noteData);

      // Actualizar tanto las notas normales como los resultados de búsqueda
      setNotes(prevNotes => 
        prevNotes.map(note => 
          note.id === editingNoteId 
            ? {
                ...note,
                message: editingNote.trim(),
                userTags: userTagsArray,
                updatedAt: new Date().toISOString(),
                updatedBy: user?.preferred_username || 'current-user',
                lastModifiedBy: user?.preferred_username || 'current-user',
                lastModifiedDate: new Date().toISOString()
              }
            : note
        )
      );

      // También actualizar los resultados de búsqueda si estamos en modo de búsqueda
      if (searchResults.length > 0) {
        setSearchResults(prevResults => 
          prevResults.map(note => 
            note.id === editingNoteId 
              ? {
                  ...note,
                  message: editingNote.trim(),
                  userTags: userTagsArray,
                  updatedAt: new Date().toISOString(),
                  updatedBy: user?.preferred_username || 'current-user',
                  lastModifiedBy: user?.preferred_username || 'current-user',
                  lastModifiedDate: new Date().toISOString()
                }
              : note
          )
        );
      }
      
      setEditingNoteId(null);
      setEditingNote('');
      setEditingUserTags('');
      
      if (onNoteUpdated) {
        onNoteUpdated(editingNoteId, editingNote.trim());
      }
    } catch (err) {
      console.error('❌ Error updating note:', err);
      setError(err.message || 'Failed to update note. Please try again.');
    }
  };

  /**
   * Show delete confirmation modal
   */
  const showDeleteConfirmation = (note) => {
    setNoteToDelete(note);
    setShowDeleteModal(true);
  };

  /**
   * Delete note (soft delete)
   */
  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {

      const token = getToken();
      
      if (!token) {
        console.error('No authentication token available');
        return;
      }

      await notesApi.softDeleteNote(noteToDelete.id, false);

      // Remove the note from local state since we don't show inactive notes
      setNotes(prevNotes => 
        prevNotes.filter(note => note.id !== noteToDelete.id)
      );
      
      // También remover de los resultados de búsqueda si existe
      setSearchResults(prevResults => 
        prevResults.filter(note => note.id !== noteToDelete.id)
      );
      
      if (onNoteDeleted) {
        onNoteDeleted(noteToDelete.id);
      }

      // Close modal and reset state
      setShowDeleteModal(false);
      setNoteToDelete(null);
      
      // Mostrar modal de éxito de eliminación
      setShowDeleteSuccessModal(true);
      
      // Cerrar automáticamente después de 3 segundos
      setTimeout(() => {
        setShowDeleteSuccessModal(false);
      }, 3000);
    } catch (err) {
      console.error('❌ Error soft deleting note:', err);
      alert('Failed to deactivate note. Please try again.');
    }
  };

  /**
   * Reactivate note
   */
  const handleReactivateNote = async (noteId) => {
    try {
      // MODIFICADO - Usar token de Keycloak via useAuth
      const token = getToken();
      
      if (!token) {
        console.error('No authentication token available');
        return;
      }

      await notesApi.softDeleteNote(noteId, true);

      // Refresh the notes list to show the reactivated note
      await fetchNotes();
      
    } catch (err) {
      console.error('❌ Error reactivating note:', err);
      alert('Failed to reactivate note. Please try again.');
    }
  };

  /**
   * Start editing a note
   */
  const startEditingNote = (note) => {
    if (!note || !note.id) return;
    
    setEditingNoteId(note.id);
    setEditingNote(note.message || '');
    // Inicializar userTags como string separado por comas
    setEditingUserTags(note.userTags ? note.userTags.join(', ') : '');
  };

  /**
   * Cancel editing
   */
  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditingNote('');
    setEditingUserTags('');
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (err) {
      return 'Invalid date';
    }
  };

  /**
   * Check if note was edited (updated after creation)
   */
  const wasNoteEdited = (note) => {
    const createdDate = note.createdDate || note.createdAt;
    const updatedDate = note.lastModifiedDate || note.updatedAt || note.updatedDate;
    
    if (!createdDate || !updatedDate) return false;
    
    try {
      const created = new Date(createdDate);
      const updated = new Date(updatedDate);
      
      // Consider edited if updated date is more than 1 second after created date
      return updated.getTime() > created.getTime() + 1000;
    } catch (err) {
      return false;
    }
  };

  /**
   * Get the last editor information
   */
  const getLastEditor = (note) => {
    return note.lastModifiedBy || note.updatedBy || note.lastModifiedBy || 'Unknown';
  };

  /**
   * Get the last edit date
   */
  const getLastEditDate = (note) => {
    return note.lastModifiedDate || note.updatedAt || note.updatedDate;
  };

  /**
   * Load notes on component mount
   */
  useEffect(() => {
    if (tag) {
      fetchNotes();
    }
  }, [tag, referenceId]);

  return (
    <>
      <Card className="shadow">
        <CardHeader className="border-0">
          <div className="d-flex justify-content-between align-items-center">
            {/* Izquierda: título e ícono */}
            <div className="d-flex align-items-center">
              <h3 className="mb-0">
                <i className="ni ni-single-copy-04 mr-2" />
                {title}
                {(searchResults.length > 0 ? searchResults.length : notes.length) > 0 && (
                  <Badge color="info" className="ml-2">
                    {searchResults.length > 0 ? searchResults.length : notes.length}
                  </Badge>
                )}
              </h3>
            </div>
  
            {/* Derecha: botones juntos */}
            <div className="d-flex align-items-center">
              <Button
                color="link"
                size="sm"
                onClick={toggleAdvancedSearch}
                className="p-0 mr-3"
              >
                <i
                  className={`ni ${
                    showAdvancedSearch ? "ni-fat-remove" : "ni-fat-add"
                  } mr-1`}
                />
                {showAdvancedSearch ? "Hide" : "Advanced"} Search
              </Button>
  
              <Button
                color="link"
                size="sm"
                onClick={toggleAddNoteForm}
                className="p-0"
              >
                <i
                  className={`ni ${
                    showAddNoteForm ? "ni-fat-remove" : "ni-fat-add"
                  } mr-1`}
                />
                {showAddNoteForm ? "Hide Note form" : "Add Note"}
              </Button>
            </div>
          </div>
        </CardHeader>
  
        <CardBody>
          {/* Error Display */}
          {error && (
            <Alert color="danger" className="mb-3">
              <i className="ni ni-support-16 mr-2" />
              {error}
            </Alert>
          )}
  
          {/* Add Note Form */}
          {showAddNoteForm && (
            <div className="mb-4">
              <Card className="shadow-sm">
                <CardBody className="p-3">
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleAddNote();
                    }}
                  >
                    <FormGroup>
                      <Label for="newNote">New Note</Label>
                      <Input
                        id="newNote"
                        type="textarea"
                        rows="3"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value.slice(0, 255))}
                        placeholder="Enter your note here..."
                        disabled={isSubmitting}
                        maxLength={255}
                      />
                    </FormGroup>
  
                    <FormGroup>
                      <Label for="newUserTags">User Tags (optional)</Label>
                      <Input
                        id="newUserTags"
                        type="text"
                        value={newUserTags}
                        onChange={(e) =>
                          handleTagInputChange(e.target.value, setNewUserTags)
                        }
                        placeholder="Enter tags separated by commas (e.g., tag1, tag2, tag3)"
                        disabled={isSubmitting}
                        maxLength={255}
                      />
                      <small className="form-text text-muted">
                        Add custom tags to categorize your note. Separate multiple
                        tags with commas. 25 characters max per tag.
                      </small>
                    </FormGroup>
  
                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        size="sm"
                        type="submit"
                        disabled={isSubmitting || !newNote.trim()}
                      >
                        {isSubmitting ? "Adding..." : "Add Note"}
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        type="button"
                        onClick={toggleAddNoteForm}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </div>
          )}
  
          {/* Advanced Search Section */}
          {showAdvancedSearch && (
            <div className="mb-4">
              <Card className="shadow-sm">
                <CardBody className="p-3">
                  <NotesSearchFilters
                    filters={searchFilters}
                    onFilterChange={handleFilterChange}
                    onSearch={handleAdvancedSearch}
                    onClear={handleClearSearch}
                    isLoading={isSearching}
                    originType={originType}
                    originId={originId}
                  />
                </CardBody>
              </Card>
            </div>
          )}
  
          {/* Notes List */}
          {loading && !hasLoaded ? (
            <div className="text-center py-4">
              <div className="text-primary">
                <i className="ni ni-loading mr-2" />
                Loading notes...
              </div>
            </div>
          ) : isSearching ? (
            <div className="text-center py-4">
              <div className="text-primary">
                <i className="ni ni-loading mr-2" />
                Searching notes...
              </div>
            </div>
          ) : searchResults.length > 0 ? (
            <div>
              <div className="mb-3 p-2 bg-light rounded">
                <small className="text-muted">
                  <i className="ni ni-zoom-scan-in mr-1" />
                  Showing {searchResults.length} search results
                  <Button
                    color="link"
                    size="sm"
                    onClick={handleClearSearch}
                    className="p-0 ml-2"
                  >
                    Clear Search
                  </Button>
                </small>
              </div>
              {searchResults.map((note) => (
                <div key={note.id} className="border rounded p-1 mb-1">
                  {editingNoteId === note.id ? (
                    <div>
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleUpdateNote();
                        }}
                      >
                        <FormGroup>
                          <Label for="editingNote">Message</Label>
                          <Input
                            id="editingNote"
                            type="textarea"
                            rows="3"
                            value={editingNote}
                            onChange={(e) => setEditingNote(e.target.value)}
                            placeholder="Enter your note here..."
                          />
                        </FormGroup>
  
                        <FormGroup>
                          <Label for="editingUserTags">User Tags (optional)</Label>
                          <Input
                            id="editingUserTags"
                            type="text"
                            value={editingUserTags}
                            onChange={(e) =>
                              handleTagInputChange(
                                e.target.value,
                                setEditingUserTags
                              )
                            }
                            placeholder="Enter tags separated by commas (e.g., tag1, tag2, tag3)"
                          />
                          <small className="form-text text-muted">
                            Add custom tags to categorize your note. Separate
                            multiple tags with commas.
                          </small>
                        </FormGroup>
  
                        <div className="d-flex gap-2">
                          <Button
                            color="success"
                            size="sm"
                            type="submit"
                            disabled={!editingNote.trim()}
                          >
                            Update
                          </Button>
                          <Button
                            color="secondary"
                            size="sm"
                            type="button"
                            onClick={cancelEditing}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Form>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2">{note.message}</div>
  
                      {/* Tags - Solo mostrar si hay userTags */}
                      {note.userTags && note.userTags.length > 0 && (
                        <div className="mb-2">
                          <small className="text-muted">
                            <i className="ni ni-tag mr-1" />
                            <span className="mr-3">
                              <strong>User Tags:</strong>{" "}
                              {note.userTags.map((tag) => (
                                <Badge
                                  key={tag}
                                  color="primary"
                                  className="mr-1 badge-sm"
                                  size="sm"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </span>
                          </small>
                        </div>
                      )}
  
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                          <div>
                            Created:{" "}
                            {formatDate(note.createdDate || note.createdAt)} by{" "}
                            {note.createdBy || "Unknown"}
                          </div>
                          {wasNoteEdited(note) && (
                            <div>
                              Last updated:{" "}
                              {formatDate(getLastEditDate(note))} by{" "}
                              {getLastEditor(note)}
                            </div>
                          )}
                        </div>
                        <div className="btn-group">
                          <Button
                            color="primary"
                            size="sm"
                            title="Edit note"
                            onClick={() => startEditingNote(note)}
                          >
                            <i className="ni ni-settings-gear-65" />
                          </Button>
                          <Button
                            color="secondary"
                            size="sm"
                            title="Delete note"
                            onClick={() => showDeleteConfirmation(note)}
                          >
                            <i className="ni ni-fat-remove" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : searchResults.length === 0 && Object.keys(searchFilters).length > 0 ? (
            <div className="text-center py-5">
              <i
                className="ni ni-zoom-split-in text-muted"
                style={{ fontSize: "3rem" }}
              ></i>
              <h4 className="mt-3 text-muted">No matches found</h4>
              <p className="text-muted">
                No notes match the current search criteria.
              </p>
              <Button
                color="primary"
                size="sm"
                onClick={handleClearSearch}
                className="mt-2"
              >
                <i className="ni ni-refresh mr-2"></i>
                Clear Search
              </Button>
            </div>
          ) : !loading && hasLoaded && notes.length === 0 ? (
            <div className="text-center py-4">
              <div className="text-muted">
                <i className="ni ni-single-copy-04 mr-2" />
                No notes found for this section. Add your first note above!
              </div>
            </div>
          ) : (
            <div>
              {notes.map((note) => (
                <div key={note.id} className="border rounded p-2 mb-2">
                  {editingNoteId === note.id ? (
                    <div>
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleUpdateNote();
                        }}
                      >
                        <FormGroup>
                          <Label for="editingNote">Message</Label>
                          <Input
                            id="editingNote"
                            type="textarea"
                            rows="3"
                            value={editingNote}
                            onChange={(e) =>
                              setEditingNote(e.target.value.slice(0, 255))
                            }
                            placeholder="Enter your note here..."
                          />
                        </FormGroup>
  
                        <FormGroup>
                          <Label for="editingUserTags">User Tags (optional)</Label>
                          <Input
                            id="editingUserTags"
                            type="text"
                            value={editingUserTags}
                            onChange={(e) =>
                              handleTagInputChange(
                                e.target.value,
                                setEditingUserTags
                              )
                            }
                            placeholder="Enter tags separated by commas (e.g., tag1, tag2, tag3)"
                          />
                          <small className="form-text text-muted">
                            Add custom tags to categorize your note. Separate
                            multiple tags with commas. 25 characters max per tag.
                          </small>
                        </FormGroup>
  
                        <div className="d-flex gap-2">
                          <Button
                            color="success"
                            size="sm"
                            type="submit"
                            disabled={!editingNote.trim()}
                          >
                            Update
                          </Button>
                          <Button
                            color="secondary"
                            size="sm"
                            type="button"
                            onClick={cancelEditing}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Form>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2">{note.message}</div>
  
                      {/* Tags - Solo mostrar si hay userTags */}
                      {note.userTags && note.userTags.length > 0 && (
                        <div className="mb-2">
                          <small className="text-muted">
                            <i className="ni ni-tag mr-1" />
                            <span className="mr-3">
                              <strong>User Tags:</strong>{" "}
                              {note.userTags.map((tag) => (
                                <Badge
                                  key={tag}
                                  color="primary"
                                  className="mr-1 ml-2 badge-sm"
                                  size="sm"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </span>
                          </small>
                        </div>
                      )}
  
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                          <div>
                            Created:{" "}
                            {formatDate(note.createdDate || note.createdAt)} by{" "}
                            {note.createdBy || "Unknown"}
                          </div>
                          {wasNoteEdited(note) && (
                            <div>
                              Last updated:{" "}
                              {formatDate(getLastEditDate(note))} by{" "}
                              {getLastEditor(note)}
                            </div>
                          )}
                        </div>
                        <div className="btn-group">
                          <Button
                            color="primary"
                            size="sm"
                            title="Edit note"
                            onClick={() => startEditingNote(note)}
                          >
                            <i className="ni ni-settings-gear-65" />
                          </Button>
                          <Button
                            color="secondary"
                            size="sm"
                            title="Delete note"
                            onClick={() => showDeleteConfirmation(note)}
                          >
                            <i className="ni ni-fat-remove" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
  
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setNoteToDelete(null);
        }}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        onConfirm={handleDeleteNote}
        confirmText="Delete"
        confirmColor="danger"
        icon="ni ni-fat-remove"
      />
  
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Note Created Successfully"
        message="Your note has been created and saved successfully."
      />
  
      {/* Delete Success Modal */}
      <SuccessModal
        isOpen={showDeleteSuccessModal}
        onClose={() => setShowDeleteSuccessModal(false)}
        title="Note Deleted Successfully"
        message="The note has been deleted successfully."
      />
    </>
  );
}


NotesPanel.propTypes = {
  tag: PropTypes.string.isRequired,
  referenceId: PropTypes.string,
  originType: PropTypes.string,
  originId: PropTypes.string,
  title: PropTypes.string,
  showAdvancedSearch: PropTypes.bool,
  systemTags: PropTypes.array,
  onNoteAdded: PropTypes.func,
  onNoteUpdated: PropTypes.func,
  onNoteDeleted: PropTypes.func,
};

export default NotesPanel;
